import { baseApi } from '../../../shared/api/baseApi';
import { ApiPath } from '../../../shared/api/types';

import type {
    AnalyticsDeleteDto,
    AnalyticsEstimateDto,
    AnalyticsExportDto,
    AnalyticsExportsPageDto,
    AnalyticsLinkDto,
} from '../model/dto';
import {
    mapAnalyticsEstimate,
    mapAnalyticsExport,
    mapAnalyticsExportsPage,
    mapAnalyticsLink,
} from '../model/mappers';
import type {
    AnalyticsDeleteResult,
    AnalyticsDownloadLink,
    AnalyticsEstimate,
    AnalyticsExport,
    AnalyticsExportsFilters,
    AnalyticsExportsPage,
    AnalyticsPeriod,
} from '../model/types';

const ANALYTICS_URL = '/api/admin/analytics' satisfies ApiPath;

const TAG = 'AnalyticsExport' as const;

const toQueryParams = (filters: AnalyticsExportsFilters) => {
    const params: Record<string, string | number> = {};

    if (filters.pageNum !== undefined) params.page_num = filters.pageNum;
    if (filters.pageSize !== undefined) params.page_size = filters.pageSize;
    if (filters.status) params.status = filters.status;
    if (filters.authorEmail) params.author_email = filters.authorEmail;
    if (filters.since) params.since = filters.since;
    if (filters.till) params.till = filters.till;

    return params;
};

export const analyticsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getAnalyticsExports: build.query<AnalyticsExportsPage, AnalyticsExportsFilters | void>({
            query: (filters) => ({
                url: ANALYTICS_URL,
                params: toQueryParams(filters ?? {}),
            }),
            transformResponse: (response: AnalyticsExportsPageDto) => mapAnalyticsExportsPage(response),
            providesTags: (result) => [
                { type: TAG, id: 'LIST' },
                ...(result?.items ?? []).map((item) => ({ type: TAG, id: item.id }) as const),
            ],
        }),

        getAnalyticsExport: build.query<AnalyticsExport, string>({
            query: (id) => ({ url: `${ANALYTICS_URL}/${id}` }),
            transformResponse: (response: AnalyticsExportDto) => mapAnalyticsExport(response),
            providesTags: (_result, _error, id) => [{ type: TAG, id }],
        }),

        estimateAnalytics: build.query<AnalyticsEstimate, AnalyticsPeriod>({
            query: ({ since, till }) => ({
                url: `${ANALYTICS_URL}/estimate`,
                params: { since, till },
            }),
            transformResponse: (response: AnalyticsEstimateDto) => mapAnalyticsEstimate(response),
        }),

        createAnalyticsExport: build.mutation<AnalyticsExport, AnalyticsPeriod>({
            query: (body) => ({
                url: ANALYTICS_URL,
                method: 'POST',
                body,
            }),
            transformResponse: (response: AnalyticsExportDto) => mapAnalyticsExport(response),
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),

        getAnalyticsLink: build.mutation<AnalyticsDownloadLink, string>({
            query: (id) => ({
                url: `${ANALYTICS_URL}/${id}/link`,
                method: 'GET',
            }),
            transformResponse: (response: AnalyticsLinkDto) => mapAnalyticsLink(response),
        }),

        deleteAnalyticsExport: build.mutation<AnalyticsDeleteResult, string>({
            query: (id) => ({
                url: `${ANALYTICS_URL}/${id}`,
                method: 'DELETE',
            }),
            transformResponse: (response: AnalyticsDeleteDto) => response,
            invalidatesTags: [{ type: TAG, id: 'LIST' }],
        }),
    }),
});

export const {
    useGetAnalyticsExportsQuery,
    useGetAnalyticsExportQuery,
    useEstimateAnalyticsQuery,
    useLazyEstimateAnalyticsQuery,
    useCreateAnalyticsExportMutation,
    useGetAnalyticsLinkMutation,
    useDeleteAnalyticsExportMutation,
} = analyticsApi;